import { useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { createColumnHelper } from '@tanstack/react-table'
import { formatDate } from 'date-fns'

import { PlusMini } from '@/components/icons'
import { PageCard, PageLayout } from '@/components/ui/page'
import {
	CellContent,
	CellText,
	CellTextHeader,
	DataTable,
	useDataTable,
} from '@/components/ui/table/data-table'
import { Text } from '@/components/ui/text'

import { useAuth } from '@/libs/hooks/use-auth'
import { toIdr } from '@/libs/utils/format'

import { itemPurchaseList } from '@/features/item/api'
import type { IItemPurchase } from '@/features/item/type'

export const Route = createFileRoute('/_app/inventory/po/')({
	component: Component,
})

function Component() {
	return (
		<PageLayout.Root>
			<PageLayout.Header
				breadcrumbs={[
					['/inventory/stock', 'Inventory'],
					['/inventory/po', 'Purchase Order (PO)'],
				]}
			/>
			<SummarySection />
			<PageCard>
				<PageCard.Header
					title="Daftar Purchase Order (PO)"
					actions={
						<PageCard.HeaderActionRoot>
							<PageCard.HeaderAction asChild>
								<Link from={Route.fullPath} to="/inventory/po/create">
									<PlusMini />
									Buat PO
								</Link>
							</PageCard.HeaderAction>
						</PageCard.HeaderActionRoot>
					}
				/>

				<POTableSection />
			</PageCard>
		</PageLayout.Root>
	)
}

// TODO
function SummarySection() {
	return null
}

function POTableSection() {
	const { outletId } = useAuth((s) => s.outlet)
	const { isLoading, data } = useQuery(itemPurchaseList.qry({ outletId }))

	const columns = useMemo(getColumns, [])
	const { table } = useDataTable({
		columns,
		data: data?.purchases ?? [],
	})

	return <DataTable table={table} columns={columns} isLoading={isLoading} />
}

const ch = createColumnHelper<IItemPurchase>()

function getColumns() {
	return [
		ch.accessor('from.name', {
			header: () => <CellTextHeader text="Supplier" />,
			cell: ({ row }) => {
				const {
					from: { name, type },
				} = row.original
				return (
					<CellContent className="flex flex-col items-start py-1">
						<Text weight="plus">{name}</Text>
						<Text size="xsmall">{type}</Text>
					</CellContent>
				)
			},
		}),
		ch.accessor('items.name', {
			header: () => <CellTextHeader text="Item" />,
			cell: ({ row }) => {
				const { items } = row.original
				return <CellText text={items.map((x) => x.name).join(', ')} />
			},
		}),
		ch.accessor('totalCost', {
			header: () => <CellTextHeader text="Biaya" />,
			cell: ({ row }) => <CellText text={toIdr(row.original.totalCost || 0)} />,
		}),
		ch.accessor('status', {
			header: () => <CellTextHeader text="Status" />,
			cell: ({ row }) => (
				<CellText
					text={<span className="capitalize">{row.original.status}</span>}
				/>
			),
		}),
		ch.accessor('createdAt', {
			header: () => <CellTextHeader text="Dibuat Pada" />,
			cell: ({ row }) => {
				const { requestedAt } = row.original
				return (
					<CellContent className="flex flex-col items-start justify-center py-1">
						<Text weight="plus" size="small">
							{formatDate(requestedAt, 'dd/MM/yyyy HH:mm')}
						</Text>
					</CellContent>
				)
			},
		}),
	]
}
